import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Loader2, Save, Zap } from 'lucide-react';
import { useCompanyPhoneNumbers } from '@/hooks/useCompanyPhoneNumbers';
import {
  useDialerSettings,
  useUpdateDialerSettings,
  type DialerSettings,
} from '@/hooks/useDialerSettings';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function PowerDialerSettings() {
  const { data: settings, isLoading, error } = useDialerSettings();
  const { data: numbers = [] } = useCompanyPhoneNumbers();
  const update = useUpdateDialerSettings();

  const [form, setForm] = useState<DialerSettings | null>(null);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const set = <K extends keyof DialerSettings>(key: K, value: DialerSettings[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Não foi possível carregar as configurações do discador.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Zap className="h-5 w-5 text-amber-500" />
            Discador Automático (Power Dialer)
          </h1>
          <p className="text-sm text-muted-foreground">
            Parâmetros globais usados nas sessões de prospecção com detecção de secretária eletrônica (AMD).
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/admin/power-dialer">Ver painel executivo</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Discagem</CardTitle>
          <CardDescription>Quantidade de linhas simultâneas, número de origem e tempo de toque.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label>Linhas simultâneas</Label>
            <Select
              value={String(form.concurrency)}
              onValueChange={(v) => set('concurrency', Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} linha{n > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Máximo de 4 para reduzir chamadas abandonadas.</p>
          </div>

          <div className="space-y-2">
            <Label>Número de origem (Caller ID)</Label>
            <Select
              value={form.from_number ?? 'auto'}
              onValueChange={(v) => set('from_number', v === 'auto' ? null : v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automático (número principal)</SelectItem>
                {numbers
                  .filter((n) => n.is_active)
                  .map((n) => (
                    <SelectItem key={n.id} value={n.phone_number}>
                      {n.label ? `${n.label} — ${n.phone_number}` : n.phone_number}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Tempo de toque (segundos)</Label>
            <Input
              type="number"
              min={10}
              max={60}
              value={form.ring_timeout_seconds}
              onChange={(e) => set('ring_timeout_seconds', Number(e.target.value))}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Secretária eletrônica (AMD)</CardTitle>
          <CardDescription>O que fazer quando a Twilio detectar uma máquina em vez de uma pessoa.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Ação ao detectar máquina</Label>
            <Select
              value={form.machine_action}
              onValueChange={(v) => set('machine_action', v as DialerSettings['machine_action'])}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hangup">Desligar imediatamente</SelectItem>
                <SelectItem value="voicemail">Deixar recado automático</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Mensagem do recado</Label>
            <Textarea
              rows={3}
              placeholder="Olá, aqui é da Divus Legal Group..."
              disabled={form.machine_action !== 'voicemail'}
              value={form.voicemail_message ?? ''}
              onChange={(e) => set('voicemail_message', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tentativas e horários</CardTitle>
          <CardDescription>Cadência de retentativas e janela permitida para discagem.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <Label>Máx. tentativas por contato</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={form.max_attempts_per_contact}
              onChange={(e) => set('max_attempts_per_contact', Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label>Intervalo entre tentativas (horas)</Label>
            <Input
              type="number"
              min={1}
              max={168}
              value={form.retry_interval_hours}
              onChange={(e) => set('retry_interval_hours', Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label>Início permitido</Label>
            <Select value={String(form.allowed_start_hour)} onValueChange={(v) => set('allowed_start_hour', Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, '0')}:00</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Fim permitido</Label>
            <Select value={String(form.allowed_end_hour)} onValueChange={(v) => set('allowed_end_hour', Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, '0')}:00</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Automações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div>
              <p className="font-medium">Criar tarefa de retorno quando não atender</p>
              <p className="text-sm text-muted-foreground">
                Gera automaticamente uma tarefa para o SDR ligar novamente ao contato sem resposta.
              </p>
            </div>
            <Switch
              checked={form.create_task_on_no_answer}
              onCheckedChange={(v) => set('create_task_on_no_answer', v)}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => update.mutate(form)} disabled={update.isPending}>
          {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Salvar configurações
        </Button>
      </div>
    </div>
  );
}
